import { commonColumns } from "$root/ui-lib/common-table-columns";
import type { ConstrainedTicker, RichTicker } from "@eu.mdotm.api/sphere";
import {
	type DialogProps,
	type TableColumn,
	AutoSortTable,
	Dialog,
	DialogFooter,
	useSelectableTableColumn,
} from "@mdotm/shadcn/legacy/components";
import { TextInput, Button } from "@mdotm/shadcn/mdotm/components";
import { type MultiSelectCtx, useSearchable } from "@mdotm/shadcn/legacy/headless";
import { toClassListRecord } from "@mdotm/shadcn/legacy/react-extensions";
import { builtInSortFnFor } from "@mdotm/shadcn/legacy/utils";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export type InstrumentDialogProps = {
	show: boolean;
	selected: Array<ConstrainedTicker>;
	instruments: Array<ConstrainedTicker>;
	onClose(): void;
	onSubmit(instruments: Array<ConstrainedTicker>): void;
	onAnimationStateChange?: DialogProps["onAnimationStateChange"];
	rowClassName?: string;
	/* ticker */
	disabledOptions?: string[];
};

export function InstrumentDialog({
	instruments,
	show,
	selected,
	rowClassName,
	onClose,
	onSubmit,
	onAnimationStateChange,
	disabledOptions,
}: InstrumentDialogProps): React.JSX.Element {
	const { t } = useTranslation();

	const dialogTitle = "Select one or more instruments";

	const [query, setQuery] = useState<string>("");
	const { filtered } = useSearchable({
		query: query,
		collection: instruments.toSorted(builtInSortFnFor("instrument")),
		extractFn: (instrument) => `${instrument.instrument}`.toLowerCase(),
	});

	const {
		column: checkBoxColumn,
		rowClassName: rowClassListFn,
		toggle,
		multiSelectCtx,
	} = useSelectableTableColumn({
		rows: filtered,
		selectBy: ({ ticker }) => ticker ?? "",
		mode: "checkbox",
		selectableRowIds:
			disabledOptions && disabledOptions.length > 0
				? instruments.filter((x) => (x.ticker ? !disabledOptions.includes(x.ticker) : false)).map((x) => x.ticker!)
				: undefined,
	});

	const multiSelectCtxRef = useRef(multiSelectCtx);
	useEffect(() => {
		const preselectedInstrumentsId = selected.flatMap((instrument) => (instrument.ticker ? [instrument.ticker] : []));
		multiSelectCtxRef.current.setSelection(preselectedInstrumentsId);
	}, [selected]);

	const columns = useMemo<Array<TableColumn<RichTicker>>>(
		() => [
			checkBoxColumn,
			{
				...commonColumns.string("instrument"),
				header: t("TABLE.HEADERS.INSTRUMENT_NAME"),
				minWidth: 144,
			},
		],
		[checkBoxColumn, t],
	);

	function onLoad(selectedTickers: MultiSelectCtx<string>, availableInstruments: Array<RichTicker>) {
		const selectedTickerIds = selectedTickers.selection.toArray();
		const selectedInstruments = selectedTickerIds.flatMap((ticker) => {
			const instrument = availableInstruments.find((availableInstrument) => availableInstrument.ticker === ticker);
			return instrument ? [instrument] : [];
		});
		onSubmit(selectedInstruments);
		onClose();
	}

	return (
		<Dialog
			size="xxlarge"
			show={show}
			onAnimationStateChange={onAnimationStateChange}
			onClose={onClose}
			header={dialogTitle}
			footer={
				<DialogFooter
					primaryAction={
						<Button palette="primary" onClick={() => onLoad(multiSelectCtx, instruments)}>
							Done
						</Button>
					}
					neutralAction={
						<Button palette="tertiary" onClick={onClose}>
							{t("BUTTON.CANCEL")}
						</Button>
					}
				/>
			}
		>
			<TextInput placeholder="Filter by name" value={query} onChangeText={setQuery} className="mb-4" />
			<AutoSortTable
				rows={filtered}
				columns={columns}
				className="max-h-[410px]"
				onRowClick={({ ticker }) => toggle(ticker ?? "")}
				rowClassName={(row, rowIndex) => ({
					...toClassListRecord(rowClassListFn(row, rowIndex)),
					[rowClassName ?? ""]: Boolean(rowClassName),
				})}
			/>
		</Dialog>
	);
}
